import React, {useContext} from 'react';

import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from '../../avatar-group.module.styl';
import {AvatarGroupContext} from '../../context';
import {AvatarWrapped} from '../avatar-wrapped/avatar-wrapped';

import type {AvatarWrappedProps} from '../avatar-wrapped/avatar-wrapped';
import type {FC} from 'react';


const classNames = withCssModule(styles);

/**
 * Props for a single avatar item within an `AvatarGroup`. Extends
 * `AvatarWrappedProps` with group-specific state.
 *
 * Size is injected automatically from the parent `AvatarGroup` via context, so
 * specifying `size` on an `AvatarItem` is not necessary.
 */
export type AvatarItemProps = {
    /**
     * Additional CSS class names applied to the avatar item wrapper element.
     */
    className?: string,
    /**
     * When `true`, applies the `avatar-group__item_active` modifier class to
     * visually highlight this avatar as selected or active (e.g. in a user
     * picker where the current assignee is highlighted).
     */
    active?: boolean,
} & AvatarWrappedProps;

/**
 * A single avatar slot inside an `<AvatarGroup>`.
 *
 * Inherits `size` from the parent `<AvatarGroup>` context — no need to pass it explicitly.
 * Use the `active` prop to highlight the currently selected or focused avatar (e.g. the
 * assigned user in a people-picker).
 */
const AvatarItem: FC<AvatarItemProps> = props => {
    const {
        className,
        active,
    } = props;

    const {size} = useContext(AvatarGroupContext);
    const classes = classNames('avatar-group__item', {'avatar-group__item_active': active}, className);

    return (
        <AvatarWrapped {...{
            size,
            className: classes,
            ...props,
        }}
        />
    );
};

AvatarItem.displayName = 'AvatarItem';

export default AvatarItem;
